import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { InputAdornment } from "@mui/material";
import { Search } from "lucide-react";

import { usePracticeDetails } from "features/practice/hooks/usePracticeDetails";
import { useBreakpoint } from "hooks/useBreakpoint";
import { pathGenerators } from "utils/routes";
import { getSignedInEmail } from "utils/signedInEmail";

import { HeaderDesktop } from "./components/HeaderDesktop/HeaderDesktop";
import { HeaderMobile } from "./components/HeaderMobile/HeaderMobile";
import styles from "./components/HeaderSearch/HeaderSearch.module.css";
import { getTitleFromPath } from "./config/headerConfig";
import { useHeaderProfile } from "./hooks/useHeaderProfile";

const DESKTOP_BREAKPOINT = 992;

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useBreakpoint(DESKTOP_BREAKPOINT);

  const nameState =
    useSelector((state) => state.auth.user?.name) ||
    localStorage.getItem("kinesin-name");
  const userState = useSelector((state) => state.user);

  const { data: staffMember, isSuccess: isProfileLoaded } = useHeaderProfile();

  // Read from the practice record rather than hardcoded, so a second practice on the same
  // build shows its own name (KIN-1141).
  // The endpoint answers with a list, and the name is `name` -- both verified against UAT
  // rather than assumed. `at(0)` matches how the referral payload reads the same hook.
  const { data: practiceDetails } = usePracticeDetails();
  const practiceName = practiceDetails?.at(0)?.name ?? "";

  // From the token rather than redux: `user.email` is never populated at login, which is
  // why the dropdown's own email line has always been blank (KIN-1141).
  const signedInEmail = useMemo(() => getSignedInEmail(), []);

  const userInitials = useMemo(() => {
    const parts = nameState ? nameState.split(" ") : [];
    return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`;
  }, [nameState]);

  const title = useMemo(
    () => getTitleFromPath(location.pathname),
    [location.pathname]
  );

  const goToPatient = useCallback(
    (path) => {
      const isSamePage = window.location.pathname === path;
      navigate(path, { state: { samePage: isSamePage } });
    },
    [navigate]
  );

  const goToPatientDetails = useCallback(
    (id) => {
      if (!id) return;
      goToPatient(pathGenerators.patient.details(id));
    },
    [goToPatient]
  );

  const goToPatientConsult = useCallback(
    (id) => {
      if (!id) return;
      goToPatient(pathGenerators.patient.consult(id));
    },
    [goToPatient]
  );

  const handleBackClick = useCallback(() => navigate(-1), [navigate]);

  const [selectedPatient, setSelectedPatient] = useState(null);

  const searchProps = {
    value: selectedPatient,
    onChange: (newValue) => {
      setSelectedPatient(newValue);
      if (newValue) goToPatientDetails(newValue.id, newValue.title);
    },
    startAdornment: (
      <InputAdornment position="start">
        <Search
          className={styles.searchIcon}
          size={20}
          strokeWidth={2}
          aria-hidden
        />
      </InputAdornment>
    ),
    extraInputProps: { maxLength: 20 },
    className: "material-input",
  };

  const profileProps = {
    staffMember: isProfileLoaded ? staffMember : null,
    nameState,
    userState,
    signedInEmail,
    userInitials,
    isLoaded: isProfileLoaded,
  };

  const navLinksProps = {
    goToPatientDetails,
    goToPatientConsult,
  };

  if (isDesktop) {
    return (
      <HeaderDesktop
        title={title}
        practiceName={practiceName}
        onBackClick={handleBackClick}
        searchProps={searchProps}
        profileProps={profileProps}
        navLinksProps={navLinksProps}
      />
    );
  }

  return <HeaderMobile searchProps={searchProps} profileProps={profileProps} />;
}

export { Header };
